<template>
	<AppDialog :closeRight="'-10'" :closeTop="'-30'" dialogName="ConfigurationsMigrationDialog" width="620" @onCancel="close">
		<ShareFeatureDialogHeader :service="service" :workflow="workflow" />
		<Col class="dialog-content px-5 pb-5 pt-4 flex-wrap flex-justify-space-between">
			<Col>
				<Row class="title-text">{{ title }}</Row>
				<Row class="desc-text mt-2"> {{ subtitle }} </Row>
				<Col>
					<h3 class="dropdown-title">Your setup values</h3>
					<MigrationDialogSelect v-model="publisherHandler" :items="publisherOptions" />
				</Col>
				<template v-if="publishedVersionHasConfiguration && workflowIsShared">
					<h3 class="dropdown-title">Other users’ setup values</h3>
					<MigrationDialogSelect v-model="allUsersHandler" :items="allUsersOptions" />
				</template>
				<Row v-if="showWarningMessage" class="warning-text mt-4">
					If your setup has new required fields, users will have to complete it before they can use the feature.
				</Row>
			</Col>
			<Row class="flex-centerize width-100 mt-4">
				<AnemicButton @click="confirmChoice" class="create-button" tile> Continue </AnemicButton>
			</Row>
		</Col>
	</AppDialog>
</template>
<script>
import { mapGettersShorthand } from '@/store/utils/mappers';
import { getServiceBySlug } from '@/store/modules/apps/getters';
import AppDialog from '@/components/dialogs/AppDialog/AppDialog.vue';
import { FROM_DRAFT, KEEP_EXISTING, LEAVE_EMPTY } from './configuration-migration-dialog.const';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import ShareFeatureDialogHeader from '@/components/dialogs/ShareFeatureDialog/ShareFeatureDialogHeader.vue';
import {
	CONFIGURATION_MIGRATION_GENERAL_KEY,
	CONFIGURATION_MIGRATION_PERSONAL_KEY,
} from '@/components/Project/publish-button-service.const';
import MigrationDialogSelect from '@/components/dialogs/ConfigurationsMigrationDialog/MigrationDialogSelect.vue';
export default {
	name: 'ConfigurationsMigrationDialog',
	components: { AppDialog, ShareFeatureDialogHeader, MigrationDialogSelect },
	props: {
		dialogPromiseHandlers: { type: Object },
		configurationType: {
			type: String,
			validator: (value) => [CONFIGURATION_MIGRATION_GENERAL_KEY, CONFIGURATION_MIGRATION_PERSONAL_KEY].includes(value),
		},
		workflow: { type: Object },
		workflowIsShared: { type: Boolean },
		publishedVersionHasConfiguration: { type: Boolean },
	},
	computed: {
		...mapGettersShorthand({ getServiceBySlug }),
		contextServiceSlug() {
			return this.workflow.context_service_slug;
		},
		title() {
			return `${this.isGeneral ? 'General' : 'Personal'} setup migration`;
		},
		subtitle() {
			return 'Setup values are per version. When you publish a new version of a feature, you need to decide what to do with the previous version’s setup values.';
		},
		service() {
			return this.contextServiceSlug ? this.getServiceBySlug(this.contextServiceSlug) : null;
		},
		isGeneral() {
			return this.configurationType === CONFIGURATION_MIGRATION_GENERAL_KEY;
		},
		publisherOptions() {
			if (this.publishedVersionHasConfiguration) {
				return [
					{
						title: 'Keep existing values from latest published version',
						value: KEEP_EXISTING,
						subTitle: 'recommended',
					},
					{ title: 'Override existing values with values from draft', value: FROM_DRAFT },
					{
						title: `Empty ${this.isGeneral ? 'general' : 'my'} setup`,
						value: LEAVE_EMPTY,
						subTitle: "you'll need to complete setup again",
					},
				];
			} else {
				return [
					{
						title: `Use ${this.configurationType} setup values from the draft I'm publishing`,
						value: FROM_DRAFT,
						subTitle: 'recommended',
					},
					{ title: `Leave ${this.isGeneral ? 'general' : 'my'} setup empty`, value: LEAVE_EMPTY },
				];
			}
		},
		showWarningMessage() {
			return [this.publisherHandler, this.allUsersHandler].some((handler) => handler === KEEP_EXISTING);
		},
		allUsersOptions() {
			return [
				{ title: 'Keep existing values from latest published version', value: KEEP_EXISTING, subTitle: 'recommended' },
				{ title: 'Empty other users’ setup', value: LEAVE_EMPTY, subTitle: 'they will need to complete setup again' },
			];
		},
		setupSource() {
			return this.isGeneral ? `in general` : 'your personal';
		},
		canKeepExisting() {
			return this.publishedVersionHasConfiguration;
		},
	},
	methods: {
		close() {
			this.dialogPromiseHandlers.reject();
		},
		confirmChoice() {
			this.dialogPromiseHandlers.resolve({ publisherHandler: this.publisherHandler, allUsersHandler: this.allUsersHandler });
			AppDialogManagerService.closeCurrentDialog();
		},
	},
	data() {
		return {
			publisherHandler: this.publishedVersionHasConfiguration ? KEEP_EXISTING : FROM_DRAFT,
			allUsersHandler: this.publishedVersionHasConfiguration && this.workflowIsShared ? KEEP_EXISTING : null,
		};
	},
};
</script>

<style scoped lang="scss">
.dialog-content {
	height: 620px;
	overflow: hidden;
	border-radius: 10px;
	background: var(--white);
}

.rounded {
	border-radius: 10px;
}

.create-button {
	width: 261px !important;
	height: 45px !important;
	font-size: 14px !important;
	color: var(--white) !important;
	border-radius: 10px !important;
	letter-spacing: 0px !important;
	font-weight: normal !important;
	text-transform: none !important;
	background: #5858ec !important;
}
.title-text {
	font-size: 18px;
	font-weight: 600;
	color: var(--primary-font-color);
}
.desc-text {
	font-size: 12px;
	color: var(--primary-font-color);
}
.radio-button-title {
	font-size: 16px;
	font-weight: 600;
}
.warning-text {
	opacity: 0.5;
	font-size: 11px;
	letter-spacing: 0px;
	color: var(--primary-font-color);
}
.dropdown-title {
	font-size: 14px;
	font-weight: 600;
	color: #424242;
	margin-top: 16px;
	letter-spacing: 0px;
	margin-bottom: 10px;
}
</style>
